import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  Box, Flex, Heading, Text, Badge, HStack, VStack, useColorModeValue,
  InputGroup, InputLeftElement, Icon, Tabs, TabList, Tab, TabPanels, TabPanel,
  Input, useToast
} from "@chakra-ui/react";
import { FaSearch } from "react-icons/fa";
import { 
  ExtractWebsiteData, 
  CharacterCounter, 
  WebPageList, 
  CustomContextList,
  CustomContextModal
} from "../../../components/DataBuilder";

export default function Data() {
  const [settings, setSettings] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("https://enqelt.com");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [checkedPages, setCheckedPages] = useState([]);
  const [customContexts, setCustomContexts] = useState([]);
  const [newCustomContext, setNewCustomContext] = useState("");
  const [newCustomContextName, setNewCustomContextName] = useState("");
  const [searchFullPage, setSearchFullPage] = useState(false); // Changed from true to false
  const [searchTerm, setSearchTerm] = useState("");
  const toast = useToast();
  
  const characterLimit = 50000;
  
  // UI Colors
  const bgColor = useColorModeValue("#f8fafc", "gray.800");
  const cardBg = useColorModeValue("white", "gray.700");
  const accentColor = useColorModeValue("blue.500", "blue.300");
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const secondaryColor = useColorModeValue("purple.500", "purple.300");
  const collapseBg = useColorModeValue("gray.50", "gray.800");
  
  const urlInputRef = useRef(null);

  // Load all data from localStorage on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('settings');
    if (savedSettings) {
      const parsedSettings = JSON.parse(savedSettings);
      setSettings(parsedSettings);
      setCustomContexts(parsedSettings.customContexts || []);
    }

    // Restore API data
    const savedApiData = localStorage.getItem('apiData');
    if (savedApiData) {
      try {
        const parsedApiData = JSON.parse(savedApiData);
        setApiData(parsedApiData);
      } catch (error) {
        console.error("Error parsing saved API data:", error);
      }
    }

    // Restore checked pages
    const savedCheckedPages = localStorage.getItem('checkedPages');
    if (savedCheckedPages) {
      try {
        const parsedCheckedPages = JSON.parse(savedCheckedPages);
        setCheckedPages(parsedCheckedPages);
      } catch (error) {
        console.error("Error parsing saved checked pages:", error);
      }
    }

    // Restore website URL
    const savedWebsiteUrl = localStorage.getItem('websiteUrl');
    if (savedWebsiteUrl) {
      setWebsiteUrl(savedWebsiteUrl);
    }

    // Restore search full page setting
    const savedSearchFullPage = localStorage.getItem('searchFullPage');
    if (savedSearchFullPage !== null) {
      try {
        setSearchFullPage(JSON.parse(savedSearchFullPage));
      } catch (error) {
        console.error("Error parsing saved search full page setting:", error);
      }
    } else {
      // If no saved setting exists, ensure it's false and save to localStorage
      setSearchFullPage(false);
      localStorage.setItem('searchFullPage', 'false');
    }
  }, []);

  const autoSelectPages = useCallback((pages) => {
    // First, find the important pages
    const mainPageIndex = pages.findIndex(page => page.url === websiteUrl);
    const headerIndex = pages.findIndex(page => page.url.toLowerCase() === 'header');
    const footerIndex = pages.findIndex(page => page.url.toLowerCase() === 'footer');
    
    // Start with the most important pages - these are mandatory
    let selectedPages = [];
    let currentCharCount = 0;
    
    // Helper to add a page if it exists and isn't already added
    const addPageIfExists = (index) => {
      if (index !== -1 && !selectedPages.includes(index)) {
        selectedPages.push(index);
        currentCharCount += pages[index].length || 0;
        return true;
      }
      return false;
    };
    
    // Add critical pages first - these are mandatory
    addPageIfExists(headerIndex);
    addPageIfExists(mainPageIndex);
    addPageIfExists(footerIndex);
    
    // Helper function to check if we have room for more content
    const hasRoomFor = (length) => currentCharCount + length <= characterLimit;
    
    // Categorize remaining pages by size
    const criticalIndices = [headerIndex, mainPageIndex, footerIndex].filter(idx => idx !== -1);
    const remainingPages = pages
      .map((page, index) => ({ 
        index, 
        length: page.length || 0,
        url: page.url 
      }))
      .filter(page => !criticalIndices.includes(page.index));
    
    // Group pages by size category
    const mediumPages = remainingPages
      .filter(page => page.length >= 1000 && page.length <= 10000)
      .sort((a, b) => a.length - b.length); // Sort smallest to largest within category
    
    const smallPages = remainingPages
      .filter(page => page.length < 1000)
      .sort((a, b) => a.length - b.length);
    
    const largePages = remainingPages
      .filter(page => page.length > 10000)
      .sort((a, b) => a.length - b.length);
    
    // First add medium pages (optimal content size)
    for (const page of mediumPages) {
      if (hasRoomFor(page.length)) {
        selectedPages.push(page.index);
        currentCharCount += page.length;
      } else {
        break; // No more room
      }
    }
    
    // Then add small pages (quick wins)
    for (const page of smallPages) {
      if (hasRoomFor(page.length)) {
        selectedPages.push(page.index);
        currentCharCount += page.length;
      } else {
        break; // No more room
      }
    }
    
    // Finally add large pages if there's still room
    for (const page of largePages) {
      if (hasRoomFor(page.length)) {
        selectedPages.push(page.index);
        currentCharCount += page.length;
      } else {
        break; // No more room
      }
    }
    
    return selectedPages;
  }, [websiteUrl, characterLimit]);

  useEffect(() => {
    // Only run this check if we have both API data and checked pages
    if (apiData.length > 0 && checkedPages.length > 0) {
      const mainPageIndex = apiData.findIndex(page => page.url === websiteUrl);
      const headerIndex = apiData.findIndex(page => page.url.toLowerCase() === 'header');
      const footerIndex = apiData.findIndex(page => page.url.toLowerCase() === 'footer');
      
      // Get critical page indices that exist but aren't currently checked
      const missingCriticalPages = [
        { index: mainPageIndex, name: "Main page" },
        { index: headerIndex, name: "Header" },
        { index: footerIndex, name: "Footer" }
      ].filter(item => item.index !== -1 && !checkedPages.includes(item.index));
      
      // If we're missing any critical pages, add them back
      if (missingCriticalPages.length > 0) {
        const updatedCheckedPages = [...checkedPages];
        
        missingCriticalPages.forEach(page => {
          updatedCheckedPages.push(page.index);
        });
        
        // Update the checked pages
        setCheckedPages(updatedCheckedPages);
        localStorage.setItem('checkedPages', JSON.stringify(updatedCheckedPages));
        
        // Inform user only if this wasn't triggered by initial load
        if (checkedPages.length > 0) {
          toast({
            title: "Essential Pages Added",
            description: `${missingCriticalPages.map(p => p.name).join(", ")} must be included in context.`,
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      }
    }
  }, [apiData, checkedPages, websiteUrl, toast]);

  useEffect(() => {
    if (apiData.length > 0) {
      if (checkedPages.length === 0) {
        const selectedPages = autoSelectPages(apiData);
        setCheckedPages(selectedPages);
        
        // Save to localStorage
        localStorage.setItem('checkedPages', JSON.stringify(selectedPages));
        
        // Show a toast notification about auto-selection
        if (selectedPages.length > 0) {
          const totalChars = selectedPages.reduce((acc, index) => acc + (apiData[index]?.length || 0), 0);
          toast({
            title: "Pages Auto-Selected",
            description: `Selected ${selectedPages.length} pages (${totalChars.toLocaleString()} characters)`,
            status: "info",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      }
      
      // Always save API data when it changes
      localStorage.setItem('apiData', JSON.stringify(apiData));
    }
  }, [apiData, websiteUrl, autoSelectPages, checkedPages.length, toast]);

  // Save checked pages whenever they change
  useEffect(() => {
    if (checkedPages.length > 0) {
      localStorage.setItem('checkedPages', JSON.stringify(checkedPages));
    }
  }, [checkedPages]);

  const handleSaveContextText = () => {
    const selectedPages = apiData.filter((_, index) => checkedPages.includes(index));
    const selectedCustomContexts = customContexts.filter((_, index) => checkedPages.includes(apiData.length + index));
    const updatedSettings = { 
      ...settings, 
      contextText: [...selectedPages.map(page => page.contextText), ...selectedCustomContexts.map(context => context.text)].join('\n\n'),
      customContexts
    };
    setSettings(updatedSettings);
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    setIsModalOpen(false);
    setIsSaved(true);

    const totalCharacters = selectedPages.reduce((acc, page) => acc + page.length, 0) + 
                           selectedCustomContexts.reduce((acc, context) => acc + context.text.length, 0);
    const totalPages = selectedPages.length + selectedCustomContexts.length;

    toast({
      title: "Context Saved Successfully",
      description: `${totalPages} pages and ${totalCharacters.toLocaleString()} characters saved to context.`,
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });

    setTimeout(() => setIsSaved(false), 2000);
  };

  // Add a button handler for auto-selection
  const handleAutoSelectPages = () => {
    if (apiData.length === 0) {
      toast({
        title: "No Pages Available",
        description: "Please extract website content first.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    const selectedPages = autoSelectPages(apiData);
    setCheckedPages(selectedPages);
    localStorage.setItem('checkedPages', JSON.stringify(selectedPages));
    
    const totalChars = selectedPages.reduce((acc, index) => acc + (apiData[index]?.length || 0), 0);
    toast({
      title: "Pages Auto-Selected",
      description: `Selected ${selectedPages.length} pages (${totalChars.toLocaleString()} characters)`,
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  const callApi = async () => {
    if (!websiteUrl.trim()) {
      toast({
        title: "URL Required",
        description: "Please enter a website URL",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    // Save the website URL to localStorage
    localStorage.setItem('websiteUrl', websiteUrl);
    // Save the search full page setting
    localStorage.setItem('searchFullPage', JSON.stringify(searchFullPage));
    
    setIsLoading(true);

    try {
      const response = await fetch(`https://9eakvq982a.execute-api.eu-north-1.amazonaws.com/?url=${websiteUrl}&search_full_page=${searchFullPage}`);
      const data = await response.json();
      
      if (!data.pages || data.pages.length === 0) {
        toast({
          title: "No Data Found",
          description: "The API returned no page data. Try a different URL or search mode.",
          status: "info",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        setApiData([]);
        // Clear stored API data
        localStorage.removeItem('apiData');
      } else {
        // Reset checked pages when loading new data
        setCheckedPages([]);
        setApiData(data.pages || []);
        // Save to localStorage
        localStorage.setItem('apiData', JSON.stringify(data.pages));
        
        toast({
          title: "Data Retrieved",
          description: `Successfully loaded ${data.pages.length} pages from ${websiteUrl}`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (error) {
      console.error('Error calling API:', error);
      toast({
        title: "Error",
        description: "Failed to fetch website data. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleCheckboxChange = (index) => {
    // Check if this is a critical page that should always be selected
    const isCriticalPage = (() => {
      if (index >= apiData.length) return false; // Not a critical page if it's a custom context
      
      const page = apiData[index];
      if (!page) return false;
      
      return (
        page.url === websiteUrl || // Main landing page
        page.url.toLowerCase() === 'header' || // Header
        page.url.toLowerCase() === 'footer' // Footer
      );
    })();
    
    // If trying to uncheck a critical page, prevent action
    if (isCriticalPage && checkedPages.includes(index)) {
      toast({
        title: "Essential Page",
        description: "Header, footer, and main page are required for proper context.",
        status: "warning",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
      return; // Prevent unchecking
    }
    
    // Otherwise proceed normally
    const newCheckedPages = prev =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index];
    
    setCheckedPages(newCheckedPages);
    
    // Update localStorage with the new checked pages
    const updatedCheckedPages = newCheckedPages(checkedPages);
    localStorage.setItem('checkedPages', JSON.stringify(updatedCheckedPages));
  };

  const handleAddCustomContext = () => {
    if (!newCustomContextName.trim() || !newCustomContext.trim()) {
      toast({
        title: "Incomplete Information",
        description: "Please provide both a name and content for your custom context.",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    
    const updatedCustomContexts = [...customContexts, { name: newCustomContextName, text: newCustomContext }];
    setCustomContexts(updatedCustomContexts);
    setNewCustomContext("");
    setNewCustomContextName("");
    setIsModalOpen(false);
    
    const updatedSettings = { ...settings, customContexts: updatedCustomContexts };
    setSettings(updatedSettings);
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    
    toast({
      title: "Custom Context Added",
      description: `"${newCustomContextName}" has been added to your contexts.`,
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  const handleRemovePage = (index, event) => {
    event.stopPropagation();
    
    const newApiData = apiData.filter((_, i) => i !== index);
    setApiData(newApiData);
    localStorage.setItem('apiData', JSON.stringify(newApiData));
    
    const newCheckedPages = checkedPages
      .filter((i) => i !== index)
      .map(i => i > index ? i - 1 : i);
    
    setCheckedPages(newCheckedPages);
    localStorage.setItem('checkedPages', JSON.stringify(newCheckedPages));
  };

  const handleRemoveCustomContext = (index, event) => {
    event.stopPropagation();
    const contextName = customContexts[index].name;
    const updatedCustomContexts = customContexts.filter((_, i) => i !== index);
    setCustomContexts(updatedCustomContexts);
    
    const newCheckedPages = checkedPages.filter((i) => i !== apiData.length + index);
    setCheckedPages(newCheckedPages);
    localStorage.setItem('checkedPages', JSON.stringify(newCheckedPages));
    
    const updatedSettings = { ...settings, customContexts: updatedCustomContexts };
    setSettings(updatedSettings);
    localStorage.setItem('settings', JSON.stringify(updatedSettings));
    
    toast({
      title: "Context Removed",
      description: `"${contextName}" has been removed from your contexts.`,
      status: "info",
      duration: 2000,
      isClosable: true,
      position: "top",
    });
  };

  const filteredApiData = apiData.filter(page => 
    page.url.toLowerCase().includes(searchTerm.toLowerCase()) || 
    page.contextText.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredCustomContexts = customContexts.filter(context => 
    context.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    context.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalCharacters = checkedPages.reduce((acc, index) => {
    if (index < apiData.length) {
      return acc + (apiData[index]?.length || 0);
    } else {
      return acc + (customContexts[index - apiData.length]?.text.length || 0);
    }
  }, 0);
  
  const totalPages = checkedPages.length;
  return (
    <Box bg={bgColor} minH="100vh" p={4}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex 
          direction={{ base: "column", md: "row" }} 
          justify="space-between" 
          align="center" 
          bg={cardBg} 
          p={5} 
          borderRadius="xl" 
          boxShadow="sm"
          borderLeft="5px solid"
          borderLeftColor={accentColor}
        >
          <VStack align="start" spacing={1}>
            <Heading size="lg" color={textColor}>Website Context Builder</Heading>
            <Text color="gray.500">Extract and customize website content for your chatbot</Text>
          </VStack>
          
          <HStack mt={{ base: 4, md: 0 }}>
            <Badge 
              colorScheme={totalCharacters > characterLimit ? "red" : "green"} 
              fontSize="md" 
              p={2} 
              borderRadius="md"
              variant="subtle"
            >
              {totalPages} pages selected
            </Badge>
          </HStack>
        </Flex>

        {/* Stats and Main Controls */}
        <Flex direction={{ base: "column", md: "row" }} gap={4}>
          {/* Extract Website Data Component */}
          <Box bg={cardBg} borderRadius="xl" boxShadow="sm" flex={1}>
            <ExtractWebsiteData 
              websiteUrl={websiteUrl}
              setWebsiteUrl={setWebsiteUrl}
              searchFullPage={searchFullPage}
              setSearchFullPage={(value) => {
                setSearchFullPage(value);
                localStorage.setItem('searchFullPage', JSON.stringify(value));
              }}
              callApi={callApi}
              isLoading={isLoading}
              accentColor={accentColor}
              urlInputRef={urlInputRef}
            />
          </Box>
          
          {/* Character Counter Component */}
          <Box bg={cardBg} borderRadius="xl" boxShadow="sm" flex={1}>
            <CharacterCounter 
              totalCharacters={totalCharacters}
              characterLimit={characterLimit}
              isSaved={isSaved}
              handleSaveContextText={handleSaveContextText}
              setIsModalOpen={setIsModalOpen}
              onAutoSelect={handleAutoSelectPages}
            />
          </Box>
        </Flex>
        
        {/* Content Area */}
        <Box>
          <Tabs variant="soft-rounded" colorScheme="blue" isLazy>
            <TabList mb={4}>
              <Tab _selected={{ color: 'white', bg: accentColor }}>Web Pages ({filteredApiData.length})</Tab>
              <Tab _selected={{ color: 'white', bg: secondaryColor }}>Custom Contexts ({filteredCustomContexts.length})</Tab>
            </TabList>
            
            <InputGroup size="md" mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={FaSearch} color="gray.400" />
              </InputLeftElement>
              <Input 
                placeholder="Search pages and content..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="md"
              />
            </InputGroup>
            
            <TabPanels>
              {/* Web Pages Tab */}
              <TabPanel p={0}>
                <WebPageList 
                  filteredApiData={filteredApiData}
                  apiData={apiData}
                  checkedPages={checkedPages}
                  handleCheckboxChange={handleCheckboxChange}
                  expandedIndex={expandedIndex}
                  toggleExpand={toggleExpand}
                  handleRemovePage={handleRemovePage}
                  cardBg={cardBg}
                  accentColor={accentColor}
                  collapseBg={collapseBg}
                  borderColor={borderColor}
                />
              </TabPanel>
              
              {/* Custom Contexts Tab */}
              <TabPanel p={0}>
                <CustomContextList 
                  filteredCustomContexts={filteredCustomContexts}
                  customContexts={customContexts}
                  apiData={apiData}
                  checkedPages={checkedPages}
                  handleCheckboxChange={handleCheckboxChange}
                  expandedIndex={expandedIndex}
                  toggleExpand={toggleExpand}
                  handleRemoveCustomContext={handleRemoveCustomContext}
                  cardBg={cardBg}
                  secondaryColor={secondaryColor}
                  collapseBg={collapseBg}
                  borderColor={borderColor}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>

      {/* Custom Context Modal */}
      <CustomContextModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        newCustomContextName={newCustomContextName}
        setNewCustomContextName={setNewCustomContextName}
        newCustomContext={newCustomContext}
        setNewCustomContext={setNewCustomContext}
        handleAddCustomContext={handleAddCustomContext}
        secondaryColor={secondaryColor}
        borderColor={borderColor}
      />
    </Box>
  );
}